import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    parentName: '',
    parentNameKana: '',
    postalCode: '',
    address: '',
    phone: '',
    email: '',
    childAge: '',
    storeLocation: '',
    privacyAgreed: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyAgreed) {
      alert('個人情報の取扱いに同意してください。');
      return;
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d4arnsb62rkouuf3q4cg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        alert('応募が完了しました。エントリー番号：' + Math.random().toString(36).substr(2, 9).toUpperCase());
        setFormData({
          parentName: '',
          parentNameKana: '',
          postalCode: '',
          address: '',
          phone: '',
          email: '',
          childAge: '',
          storeLocation: '',
          privacyAgreed: false
        });
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-green-100">
      {/* ヒーローセクション */}
      <section className="relative min-h-screen bg-gradient-to-br from-green-600 via-green-500 to-green-400 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* 雪の結晶アニメーション */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 text-white/30 text-2xl animate-pulse">❄</div>
          <div className="absolute top-40 right-20 text-white/20 text-3xl animate-bounce">✨</div>
          <div className="absolute top-60 left-1/4 text-white/25 text-xl animate-pulse">❄</div>
          <div className="absolute bottom-40 right-1/3 text-white/30 text-2xl animate-bounce">⭐</div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="text-center text-white">
            {/* 応募締切バッジ */}
            <div className="inline-block bg-red-500 text-white px-6 py-2 rounded-full font-bold text-lg mb-8 shadow-lg">
              応募締切 12/14（日）まで
            </div>

            {/* メインタイトル */}
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              生誕20周年記念
              <br />
              <span className="text-yellow-300">いーなくん</span>
              <br className="md:hidden" />
              <span className="md:inline block">クリスマスケーキ プレゼント</span>
            </h1>

            {/* サブコピー */}
            <p className="text-xl md:text-2xl mb-12 font-semibold">
              抽選で<span className="text-yellow-300 text-3xl font-bold">100名様</span>に
              <br />
              "5号（15cm）クリスマスケーキ"が当たる！
            </p>

            {/* CTAボタン */}
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
              <a 
                href="https://lin.ee/VikYXbC"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-line-fill mr-2"></i>
                LINEで応募
              </a>
              <a 
                href="https://pro.form-mailer.jp/fms/ecbd60bc343117"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-400 hover:bg-yellow-500 text-green-800 px-8 py-4 rounded-full font-bold text-lg shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line mr-2"></i>
                応募フォームへ
              </a>
            </div>

            {/* ビジュアル */}
            <div className="mt-16">
              <img 
                src="https://static.readdy.ai/image/be26272330b519536e090c1a73280173/e10af1eb419997d88dd7826e8bad6f79.jpeg"
                alt="いーなくんとクリスマスケーキ"
                className="mx-auto rounded-2xl shadow-2xl object-cover w-full max-w-4xl aspect-[4/1.5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* いーなくん紹介セクション */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-700 mb-8">いーなくんって？</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              20年前にクーポン雑誌キャラクターとしてスタートして今は子供向けキャラクターとしていろんな場面で活躍してます。
            </p>
          </div>

          {/* 検索キーワード */}
          <div className="text-center">
            <div className="inline-block bg-yellow-100 border-2 border-yellow-300 rounded-lg px-6 py-3">
              <span className="text-green-700 font-semibold">検索キーワード：</span>
              <span className="text-green-800 font-bold">いーなくん20周年</span>
            </div>
          </div>
        </div>
      </section>

      {/* プレゼント内容セクション */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">プレゼント内容</h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://static.readdy.ai/image/be26272330b519536e090c1a73280173/a0a83599e57e1afc32c08d0526058892.jpeg"
                alt="クリスマスケーキ 5号"
                className="rounded-2xl shadow-lg w-full object-cover"
              />
            </div>
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-green-700 mb-6">本賞</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-gift-line text-red-500 text-2xl mr-3"></i>
                    <span className="text-lg">クリスマスケーキ 5号（15cm）</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-green-500 text-2xl mr-3"></i>
                    <span className="text-lg">抽選100名様</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-store-line text-blue-500 text-2xl mr-3"></i>
                    <span className="text-lg">指定ドコモショップ店舗で引換</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-calendar-line text-purple-500 text-2xl mr-3"></i>
                    <span className="text-lg">12/24（水）13:00〜18:00受取</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-100 rounded-2xl p-6 mt-6">
                <h4 className="text-lg font-bold text-green-700 mb-3">Wチャンス</h4>
                <p className="text-green-700">いーなくんミニタオル または いーなくん絵本</p>
              </div>
            </div>
          </div>

          {/* 受取店舗 */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-green-700 text-center mb-8">受取店舗</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {['高知インター店', '土佐道路店', '土佐店', '四万十西店'].map((store, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-lg text-center">
                  <i className="ri-store-2-line text-3xl text-green-500 mb-3"></i>
                  <p className="text-sm text-gray-600">ドコモショップ</p>
                  <h4 className="font-bold text-green-700 mt-2">{store}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 応募概要セクション */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">応募概要</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-700 mb-4">応募対象</h3>
                <p className="text-gray-700">高知県在住で小学生未満（0〜6歳）のお子さまがいるご家庭</p>
              </div>
              
              <div className="bg-green-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-700 mb-4">応募期間</h3>
                <p className="text-gray-700">〜 12/14（日）当日消印有効<br />（フォーム／LINEも同日23:59迄）</p>
              </div>
              
              <div className="bg-green-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-700 mb-4">当選発表</h3>
                <p className="text-gray-700">抽選の上、当選通知（郵送またはLINE/メール）でご案内<br />（目安：12/16頃）</p>
              </div>
              
              <div className="bg-green-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-700 mb-4">応募方法</h3>
                <ul className="text-gray-700 space-y-1">
                  <li>① LINE公式アカウントから</li>
                  <li>② Web応募フォームから</li>
                  <li>③ ハガキ（チラシ付属）</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mt-8">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">注意事項</h3>
              <ul className="text-yellow-700 space-y-2 text-sm">
                <li>• 1家族1口まで／当選は1回まで</li>
                <li>• 転売・譲渡・換金不可</li>
                <li>• やむを得ない事情により中止・変更の可能性あり</li>
                <li>• 応募条件を満たさない場合は無効</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 応募導線セクション */}
      <section id="application" className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">応募方法</h2>
          <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto">
            LINE、応募フォーム、応募専用ハガキのいずれかでご応募ください。
          </p>
          
          <div className="max-w-4xl mx-auto">
            {/* LINEで応募 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center mb-8">
              <h3 className="text-2xl font-bold text-green-700 mb-6">LINEで簡単応募</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-center space-x-4">
                  <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="font-bold text-green-700">1</span>
                  </div>
                  <span className="text-lg">友だち追加</span>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="font-bold text-green-700">2</span>
                  </div>
                  <span className="text-lg">申込リンクをタップ</span>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="font-bold text-green-700">3</span>
                  </div>
                  <span className="text-lg">必要事項入力・送信</span>
                </div>
              </div>
              <div className="mt-8">
                <img 
                  src="https://static.readdy.ai/image/be26272330b519536e090c1a73280173/f2d201076b71414fc96871bdfa46c290.png"
                  alt="LINE QRコード"
                  className="mx-auto w-48 h-48 object-cover rounded-lg"
                />
                <a 
                  href="https://lin.ee/VikYXbC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg mt-6 shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-line-fill mr-2"></i>
                  友だち追加
                </a>
              </div>
            </div>

            {/* Webフォームで応募 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center mb-8">
              <h3 className="text-2xl font-bold text-green-700 mb-6">Webフォームで応募</h3>
              <p className="text-gray-700 mb-8">下記のフォームに必要事項をご入力ください</p>
              <a 
                href="https://pro.form-mailer.jp/fms/ecbd60bc343117"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-400 hover:bg-yellow-500 text-green-800 px-8 py-4 rounded-full font-bold text-lg shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line mr-2"></i>
                フォームへ
              </a>
            </div>

            {/* ハガキ応募 */}
            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-700 mb-6 text-center">ハガキ応募手順</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-edit-line text-2xl text-blue-700"></i>
                  </div>
                  <h4 className="font-bold text-blue-700 mb-2">記入</h4>
                  <p className="text-sm text-blue-600">チラシの応募欄に必要事項を記入</p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-copy-line text-2xl text-blue-700"></i>
                  </div>
                  <h4 className="font-bold text-blue-700 mb-2">控え記入</h4>
                  <p className="text-sm text-blue-600">応募控えに同じ内容を記入</p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-send-line text-2xl text-blue-700"></i>
                  </div>
                  <h4 className="font-bold text-blue-700 mb-2">投函</h4>
                  <p className="text-sm text-blue-600">12/14（日）当日消印有効</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 受取方法セクション */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">受取方法（当選後の流れ）</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-text-line text-3xl text-green-700"></i>
                </div>
                <h3 className="font-bold text-green-700 mb-2">応募</h3>
                <p className="text-sm text-gray-600">フォーム・LINE・ハガキで応募</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shuffle-line text-3xl text-green-700"></i>
                </div>
                <h3 className="font-bold text-green-700 mb-2">抽選</h3>
                <p className="text-sm text-gray-600">厳正な抽選を実施</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-green-700"></i>
                </div>
                <h3 className="font-bold text-green-700 mb-2">当選通知</h3>
                <p className="text-sm text-gray-600">引換券をお送りします</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-store-line text-3xl text-green-700"></i>
                </div>
                <h3 className="font-bold text-green-700 mb-2">店舗で受取</h3>
                <p className="text-sm text-gray-600">指定店舗でケーキ受取</p>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mt-12">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">当日持参物・注意事項</h3>
              <ul className="text-yellow-700 space-y-2">
                <li>• 当選通知ハガキまたは当選画面</li>
                <li>• 応募控え（ハガキ応募の場合）</li>
                <li>• 時間を過ぎた場合は無効</li>
                <li>• 日付・店舗変更不可</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* よくある質問セクション */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">よくある質問</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "応募は何口まで？",
                answer: "1家族1口です。"
              },
              {
                question: "当選連絡はいつ？",
                answer: "12/16頃を予定（目安）しております。"
              },
              {
                question: "受取に必要なもの？",
                answer: "当選通知（または応募控え）をお持ちください。"
              },
              {
                question: "対象外年齢・県外在住は？",
                answer: "応募無効となります。"
              },
              {
                question: "店舗／日時の変更は？",
                answer: "変更はできません。"
              }
            ].map((faq, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-6">
                <h3 className="font-bold text-green-700 mb-2">Q. {faq.question}</h3>
                <p className="text-gray-700">A. {faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* お問い合わせセクション */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-green-700 text-center mb-16">お問い合わせ</h2>
          
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-green-700 mb-6">LINE公式アカウントからお問合せください</h3>
              <div className="space-y-6">
                <img 
                  src="https://static.readdy.ai/image/be26272330b519536e090c1a73280173/f2d201076b71414fc96871bdfa46c290.png"
                  alt="LINE QRコード"
                  className="mx-auto w-48 h-48 object-cover rounded-lg"
                />
                <a 
                  href="https://lin.ee/VikYXbC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-line-fill mr-2"></i>
                  友だち追加してお問合せ
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-green-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">いーなくん20周年記念</h3>
            <p className="text-green-200 mb-8">みんなの"いいな！"をつないで20年</p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <a href="/privacy" className="text-green-200 hover:text-white transition-colors">プライバシーポリシー</a>
            </div>
            
            <div className="border-t border-green-600 pt-6">
              <p className="text-green-200 text-sm">
                © 2024 いーなくん20周年記念キャンペーン. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
